import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Bookmark, 
  FolderOpen, 
  Tag, 
  TrendingUp, 
  Shield,
  Database,
  Clock,
  Mail,
  Chrome,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPage = ({ onBack }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        credentials: 'include'
      });

      if (response.status === 403) {
        toast.error('Acesso negado. Você não tem permissão de administrador.');
        onBack();
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch dashboard');

      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
      toast.error('Erro ao carregar dados do admin');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-r-transparent mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando painel admin...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground">Acesso Negado</h2>
          <p className="text-muted-foreground mt-2">Você não tem permissão para acessar esta área.</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const { user_stats, content_stats, recent_users, system_info } = dashboard;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold text-foreground">Painel Administrativo</h1>
            </div>
          </div>
          <button
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Users Card */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm" data-testid="stat-users">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <span className="text-xs text-muted-foreground bg-accent px-2 py-1 rounded-full">
                Total
              </span>
            </div>
            <h3 className="text-3xl font-bold text-foreground">{user_stats.total_users}</h3>
            <p className="text-sm text-muted-foreground mt-1">Usuários</p>
            <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <Mail className="w-3 h-3 text-muted-foreground" />
                <span className="text-foreground">{user_stats.email_users} Email</span>
              </div>
              <div className="flex items-center gap-1">
                <Chrome className="w-3 h-3 text-muted-foreground" />
                <span className="text-foreground">{user_stats.google_users} Google</span>
              </div>
            </div>
          </div>

          {/* Bookmarks Card */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm" data-testid="stat-bookmarks">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Bookmark className="w-6 h-6 text-green-500" />
              </div>
              <span className="text-xs text-muted-foreground bg-accent px-2 py-1 rounded-full">
                Total
              </span>
            </div>
            <h3 className="text-3xl font-bold text-foreground">{content_stats.total_bookmarks}</h3>
            <p className="text-sm text-muted-foreground mt-1">Favoritos</p>
            <div className="mt-4 pt-4 border-t border-border text-xs">
              <div className="flex items-center gap-1 text-green-500">
                <TrendingUp className="w-3 h-3" />
                <span>+{content_stats.bookmarks_last_7_days} últimos 7 dias</span>
              </div>
            </div>
          </div>

          {/* Categories Card */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm" data-testid="stat-categories">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <FolderOpen className="w-6 h-6 text-purple-500" />
              </div>
              <span className="text-xs text-muted-foreground bg-accent px-2 py-1 rounded-full">
                Total
              </span>
            </div>
            <h3 className="text-3xl font-bold text-foreground">{content_stats.total_categories}</h3>
            <p className="text-sm text-muted-foreground mt-1">Categorias</p>
          </div>

          {/* Tags Card */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm" data-testid="stat-tags">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Tag className="w-6 h-6 text-orange-500" />
              </div>
              <span className="text-xs text-muted-foreground bg-accent px-2 py-1 rounded-full">
                Total
              </span>
            </div>
            <h3 className="text-3xl font-bold text-foreground">{content_stats.total_tags}</h3>
            <p className="text-sm text-muted-foreground mt-1">Tags</p>
          </div>
        </div>

        {/* Activity & Users Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Overview */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Atividade
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">Ativos (7 dias)</span>
                </div>
                <span className="font-semibold text-foreground">{user_stats.active_last_7_days}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">Ativos (30 dias)</span>
                </div>
                <span className="font-semibold text-foreground">{user_stats.active_last_30_days}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bookmark className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">Favoritos (30 dias)</span>
                </div>
                <span className="font-semibold text-foreground">{content_stats.bookmarks_last_30_days}</span>
              </div>
            </div>
          </div>

          {/* Users List */}
          <div className="lg:col-span-2 bg-card rounded-xl p-6 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Usuários Recentes
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Usuário</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Favoritos</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Role</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Criado</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_users.map((user) => (
                    <tr key={user.id} className="border-b border-border/50 hover:bg-accent/30 transition-colors">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {(user.name || user.email)[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground truncate max-w-[200px]">
                              {user.name || 'Sem nome'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {user.google_linked ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full text-xs">
                            <Chrome className="w-3 h-3" />
                            Google
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-500/10 text-gray-500 rounded-full text-xs">
                            <Mail className="w-3 h-3" />
                            Email
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-sm text-foreground">{user.bookmark_count}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {user.is_admin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 text-amber-500 rounded-full text-xs font-medium">
                            <Shield className="w-3 h-3" />
                            {user.role || 'Admin'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Usuário</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(user.created_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="mt-6 bg-card rounded-xl p-6 border border-border shadow-sm">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Informações do Sistema
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-accent/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Banco de Dados</p>
              <p className="text-sm font-medium text-foreground mt-1">{system_info.database}</p>
            </div>
            <div className="p-4 bg-accent/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Ambiente</p>
              <p className="text-sm font-medium text-foreground mt-1 capitalize">{system_info.environment}</p>
            </div>
            <div className="p-4 bg-accent/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Versão API</p>
              <p className="text-sm font-medium text-foreground mt-1">{system_info.api_version}</p>
            </div>
            <div className="p-4 bg-accent/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Collections</p>
              <p className="text-sm font-medium text-foreground mt-1">
                {Object.keys(system_info.collections || {}).length} tabelas
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
